/**
 * Listens for keyboard-wedge scanner bursts on the page.
 *
 * - Listens in the capture phase, so a scan is seen before the focused field's own handlers.
 * - When a burst completes, the terminator key is suppressed (no accidental form submit) and the
 *   characters the scanner already typed into the focused field are removed.
 * - A focused element marked `data-headlight-qr-pick` owns the scan: its value becomes the decoded
 *   code and it fires `change`, instead of the scan going to the page's scan handlers.
 * - Elements marked `data-headlight-qr-wedge="ignore"` (and password fields) are never touched.
 */

const WedgeBurstDetector = require('./Wedge-Burst-Detector.js');

class WedgeScanSource
{
	/**
	 * @param {Object} pOptions
	 * @param {(pText: string, pDetails: { Target: Element|null, Pick: boolean, PickElement: Element|null, MeanIntervalMS?: number }) => void} pOptions.onScan
	 * @param {import('./Wedge-Burst-Detector.js').WedgeOptions} [pOptions.Detector]
	 * @param {Document} [pOptions.Document]
	 */
	constructor(pOptions)
	{
		this.onScan = pOptions.onScan;
		this.document = pOptions.Document || (typeof document !== 'undefined' ? document : null);
		this.detector = new WedgeBurstDetector(pOptions.Detector);
		this.enabled = false;
		/** @type {Array<{ Key: string, Time: number, State: string }>} recent keys, for diagnostics */
		this.recentKeys = [];
		this._handleKeyDown = (pEvent) => this.handleKeyDown(pEvent);
	}

	enable()
	{
		if (this.enabled || !this.document)
		{
			return;
		}
		this.document.addEventListener('keydown', this._handleKeyDown, true);
		this.enabled = true;
	}

	disable()
	{
		if (!this.enabled || !this.document)
		{
			return;
		}
		this.document.removeEventListener('keydown', this._handleKeyDown, true);
		this.detector.reset();
		this.enabled = false;
	}

	/**
	 * @param {KeyboardEvent} pEvent
	 */
	handleKeyDown(pEvent)
	{
		const tmpTarget = /** @type {any} */ (pEvent.target);
		if (tmpTarget && tmpTarget.closest && (tmpTarget.closest('[data-headlight-qr-wedge="ignore"]') || tmpTarget.type === 'password'))
		{
			this.detector.reset();
			return;
		}
		const tmpTime = (typeof pEvent.timeStamp === 'number' && pEvent.timeStamp > 0) ? pEvent.timeStamp : Date.now();
		const tmpResult = this.detector.handleKey(pEvent.key, tmpTime);
		this.recentKeys.push({ Key: pEvent.key, Time: tmpTime, State: tmpResult.State });
		if (this.recentKeys.length > 60)
		{
			this.recentKeys.shift();
		}
		if (tmpResult.Consume)
		{
			pEvent.preventDefault();
			pEvent.stopPropagation();
		}
		if (tmpResult.State !== 'complete')
		{
			return;
		}

		const tmpPickElement = (tmpTarget && tmpTarget.closest) ? tmpTarget.closest('[data-headlight-qr-pick]') : null;
		this.removeTypedText(tmpTarget, tmpResult.Value);
		this.onScan(tmpResult.Value, { Target: tmpTarget || null, Pick: !!tmpPickElement, PickElement: tmpPickElement, MeanIntervalMS: tmpResult.MeanIntervalMS });
	}

	/**
	 * Remove the scanned characters the scanner typed into an editable field before the burst was recognised.
	 *
	 * @param {any} pTarget
	 * @param {string} pValue
	 */
	removeTypedText(pTarget, pValue)
	{
		if (!pTarget || typeof pTarget.value !== 'string' || !pValue)
		{
			return;
		}
		if (pTarget.value.endsWith(pValue))
		{
			pTarget.value = pTarget.value.slice(0, pTarget.value.length - pValue.length);
		}
	}
}

module.exports = WedgeScanSource;
